<template>
  <page-wrapper :has-pad="true" title="รายละเอียดคูปอง">
    <loading-overlay :active="isLoading || isSubmitting" />
    <sc-button :route="usageRoute" class="mb-4" variant="secondary" size="sm"
      >ประวัติการใช้งาน</sc-button
    >
    <coupon-editor v-if="form" v-model="form" :disable-project-delete-all="true"></coupon-editor>
    <div class="row mt-5">
      <div class="col-6">
        <sc-button variant="danger" :disabled="isSubmitting" @click="handleClickRemoveButton">ลบ</sc-button>
      </div>
      <div class="col-6">
        <sc-button
          class="btn-submit"
          variant="primary"
          :disabled="isSubmitting"
          @click="handleClickSubmitButton"
        >บันทึก</sc-button>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon";
import { MessageBox } from "element-ui";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import LoadingOverlay from "@/lib/components/LoadingOverlay";
import { baanRueJaiClient } from "@/dependencies";
import { toOptionalISOString } from "@/lib/dateTime";
import CouponEditor from "../components/CouponEditor";

export default {
  name: "CouponDetailPage",
  components: {
    PageWrapper,
    CouponEditor,
    ScButton,
    LoadingOverlay
  },
  data() {
    const coupon = null;
    const form = null;

    return {
      coupon,
      form,
      isLoading: false,
      isSubmitting: false
    };
  },
  computed: {
    usageRoute() {
      const id = this.getRouteCouponId();
      if (!id) {
        return { name: "CouponListPage" };
      }

      return { name: "CouponUsagePage", params: { id } };
    },
  },
  created() {
    this.setupData();
  },
  methods: {
    getRouteCouponId() {
      const { id } = this.$route.params;
      if (!id) {
        this.$message.error("ไม่พบรหัสคูปอง");
        return null;
      }

      return id;
    },
    async setupData() {
      this.isLoading = true;
      try {
        await this.setupCoupon();
        await this.setupForm();
      } catch (error) {
        this.$message.error("ไม่สามารถโหลดข้อมูลคูปองได้");
      } finally {
        this.isLoading = false;
      }
    },
    async setupCoupon() {
      const coupon = await this.fetchCoupon();
      if (!coupon) {
        return;
      }

      this.coupon = coupon;
    },
    async setupForm() {
      if (!this.coupon) {
        return;
      }

      const {
        code,
        effective_date: effectiveDateISO,
        expiry_date: expiryDateISO,
        usage_quota: usageQuota,
        usage_quota_per_user: usageQuotaPerUser,
        minimum_purchased_amount: minimumPurchasedAmount,
        maximum_discount_amount: maximumDiscountAmount,
        selected_packages: selectedPackages,
        discount_amount: discountAmount,
        discount_percent_amount: discountPercentAmount,
        supported_project_ids: supportedProjectIds,
        expired_days_after_transfered: expiredDaysAfterTransfered,
        is_first_time_only: isFirstTimeOnly,
        is_expired: isExpired,
        excluded_packages: excludedPackagesData
      } = this.coupon;

      const effectiveDate = DateTime.fromISO(effectiveDateISO);
      const expiryDate = DateTime.fromISO(expiryDateISO);

      // The coupon endpoint summarises each package's services and projects as counts;
      // it does not send the underlying id arrays.
      const excludedPackages = excludedPackagesData
        && excludedPackagesData.packages
        && excludedPackagesData.packages.length > 0
        ? excludedPackagesData.packages.map(item => {
        const {
          id: itemId,
          name,
          price,
          billing_cycle: billingCycle,
          detail,
          service_id_count: serviceIdCount,
          project_id_count: projectIdCount,
          image: imageUrl,
          target,
        } = item;

        return {
          id: itemId.toString(),
          name,
          target,
          price,
          billingCycle,
          detail,
          serviceCount: serviceIdCount ?? 0,
          projectCount: projectIdCount ?? 0,
          imageUrl,
          hasSelected: false
        };
      })
      : null

      this.form = {
        code,
        effectiveDate,
        expiryDate,
        expiredDaysAfterTransfered,
        usageQuota,
        usageQuotaPerUser,
        minimumPurchasedAmount,
        maximumDiscountAmount,
        selectedPackages: selectedPackages.packages.map(item => {
          const {
            id: pkgId,
            name,
            price,
            billing_cycle: billingCycle,
            detail,
            service_id_count: serviceIdCount,
            project_id_count: projectIdCount,
            image: imageUrl,
            target,
          } = item;

          return {
            id: pkgId.toString(),
            name,
            target,
            price,
            billingCycle,
            detail,
            serviceCount: serviceIdCount ?? 0,
            projectCount: projectIdCount ?? 0,
            imageUrl,
            hasSelected: true
          };
        }),
        discountAmount,
        discountPercentAmount,
        supportedProjectIds,
        isFirstTimeOnly,
        isActionEdit: true,
        isExpired,
        excludedPackages
      };
    },
    async fetchCoupon() {
      const id = this.getRouteCouponId();
      if (!id) {
        return null;
      }

      const response = await baanRueJaiClient.admin.subscriptions.coupons.get(
        id
      );

      return response.data.result;
    },
    async handleClickRemoveButton() {
      try {
        await MessageBox.confirm("ยืนยันการลบคูปอง", { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" });
      } catch { return; }
      const id = this.getRouteCouponId();
      if (!id) {
        return;
      }
      this.isSubmitting = true;
      try {
        await baanRueJaiClient.admin.subscriptions.coupons.delete(id);
        this.$router.push({ name: "CouponListPage" });
      } catch (error) {
        this.$message.error(error.response?.data?.message ?? "ไม่สามารถลบคูปองได้");
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleClickSubmitButton() {
      this.isSubmitting = true;
      try {
        await this.updateCoupon();
        this.$router.push({ name: "CouponListPage" });
      } catch (error) {
        this.$message.error(error.response.data.message);
      } finally {
        this.isSubmitting = false;
      }
    },
    async updateCoupon() {
      const id = this.getRouteCouponId();
      if (!id) {
        return null;
      }

      const params = {
        code: this.form.code,
        expiryDate: toOptionalISOString(this.form.expiryDate),
        selectedPackageIds: this.form.selectedPackages.map(({ id: pkgId }) => +pkgId),
        supportedProjectIds: this.form.supportedProjectIds,
        expiredDaysAfterTransfered: this.form.expiredDaysAfterTransfered
      };

      const response = await baanRueJaiClient.admin.subscriptions.coupons.put(
        id, params
      );

      return response.data.result;
    },
  }
};
</script>

<style lang="scss" scoped>
.btn-submit {
  float: right;
}
</style>
